"use client";

import React from "react";
import Image from "next/image";
import { unstable_ViewTransition as ViewTransition } from "react";
import MethodCards from "@/components/utils/MethodCards";
import { InView } from "react-intersection-observer";
import NewsList from "@/components/utils/NewsList";
import AccordionLarge from "@/components/utils/AccordionLarge";
import AnchorLink from "@/components/utils/AnchorLink";
import InViewAnimation from "@/components/utils/InViewAnimation";
import Link from "next/link";
import SVG from "react-inlinesvg";

const MethodSection = ({posts}) => {
  return (
    <main className="page page-method-detail page-method-soil">
      <InView>
        {({ inView, ref, entry }) => (
          <section ref={ref} className={`section-method-kv container-l ${inView ? "inViewDark" : ""}`}>
            <ViewTransition name="method-card-container-1">
              <div className="method-card">
                <div className="method-card-image">
                  <img src="/assets/img/method/soil/kv-pic.jpg" alt="" />
                </div>
                <div className="method-card-inner">
                  <div className="method-card-header">
                    <div className="badge en">
                      <span className="text">Method</span>
                      <span className="number">01</span>
                    </div>
                    <div className="method-card-shoulder bold">おいしく育つ土づくり</div>
                    <h1 className="method-card-title en">
                      Soil Solution
                    </h1>
                    <div className="method-card-description">
                      <div className="anchor-list">
                        <AnchorLink href="#SECTION-1" className="inner-link scaleHover">
                          <span>
                            <i className="icon">
                              <SVG src="/assets/img/common/icon/icon-arrow-anchor.svg" />
                            </i>
                            Soil Solutionとは
                          </span>
                        </AnchorLink>
                        <AnchorLink href="#SECTION-2" className="inner-link scaleHover">
                          <span>
                            <i className="icon">
                              <SVG src="/assets/img/common/icon/icon-arrow-anchor.svg" />
                            </i>
                            オールインワン有機発酵資材「Soil Next」
                          </span>
                        </AnchorLink>
                        <AnchorLink href="#SECTION-3" className="inner-link scaleHover">
                          <span>
                            <i className="icon">
                              <SVG src="/assets/img/common/icon/icon-arrow-anchor.svg" />
                            </i>
                            国立研究開発法人 理化学研究所｜自然界の多様性を畑に再現する意義
                          </span>
                        </AnchorLink>
                      </div>
                      <p className="anotation">北海道本別町の福田農場にて。<br />多様な有機物を発酵、熟成させた、Soil Next。</p>
                    </div>
                  </div>
                </div>
              </div>
            </ViewTransition>
          </section>
        )}
      </InView>

      <div className="container-m">
        <section className="section section-gallery">
          <div className="gallery-title bold">
            収量もしっかり維持しながら、<br />
            自然な多様性を持つ健康な土へ。
          </div>
          <div className="pic-block size-l">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/method/soil/gallery/pic-1.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                北海道更別村の松橋農場。およそ4.5ha(ヘクタール)のじゃがいも畑。
              </div>
            </InViewAnimation>
          </div>
        </section>

        <div className="separator-asterisk en">*</div>

        <section id="SECTION-1" className="section section-method-soil-1">
          <div className="section-title-holder">
            <h2 className="section-title bold">
              Soil Solutionとは
            </h2>
          </div>

          <div className="block n1 sticky-block-container grid-3-col">
            <div className="sticky-item border block-title-holder">
              <h2 className="block-title bold">
                自然界の多様な生態系を<br />
                畑の土壌に再現
              </h2>
              <div className="block-lead bold">
                自然界の多様な有機物とミネラルを発酵させた独自の有機発酵資材「Soil Next」を軸に、単一栽培となってしまう畑においても、自然界の多様な土壌生態系へと近づけていく。そうすることで、収量を維持しながら化学肥料や農薬を減らし、野菜はよりおいしく、地球環境への負荷は小さく、気候変動へのリスク耐性も高い自立安定的な農業へのシフトを可能にします。
              </div>
            </div>
            <div className="static-item">
              <picture className="pic sp-wide">
                 <source srcSet="/assets/img/method/soil/section-1-pic.png" media="(min-width: 1024px)" />
                  <source srcSet="/assets/img/method/soil/section-1-pic-sp.png" media="(max-width: 1024px)" />
                <img src="/assets/img/method/soil/section-1-pic.png" alt="" loading="lazy" />
              </picture>
            </div>
          </div>
        </section>

        <div className="separator-asterisk en">*</div>

        <section id="SECTION-2" className="section section-method-soil-2">
          <div className="section-title-holder">
            <h2 className="section-title bold">
              <span className="small">3年で健康な土の基礎をつくる<br className="spOnly" />オールインワン有機発酵資材</span>
              Soil Next
            </h2>
          </div>

          <div className="block n1 sticky-block-container grid-3-col">
            <div className="sticky-item border block-title-holder">
              <div className="block-lead bold">
                年に1回、畑に入れるだけで、自然界の多様な土壌生態系を再現する。それがSoil Next（ソイルネクスト）。<br />
                誰もが実践可能で、どんな農法にも対応できる。これまでにない、土から健康な農業へ導くオールインワン資材です。<br />
                Soil Nextは、自然界の植物や動物、海藻や鉱物など、山や海からの多様な有機物とミネラルを原料として、2段階の発酵過程の後に熟成。<br />
                多様な微生物と活性の高い腐植に富んだ有機発酵資材です。年1回、10アールあたり1tのSoil Nextの投入により、3年で土の基礎をつくります。
              </div>
            </div>
            <div className="static-item">
              <picture className="pic sp-wide">
                <source srcSet="/assets/img/method/soil/section-2-pic.png" media="(min-width: 1024px)" />
                <source srcSet="/assets/img/method/soil/section-2-pic-sp.png" media="(max-width: 1024px)" />
                <img src="/assets/img/method/soil/section-2-pic.png" alt="" loading="lazy" />
              </picture>
            </div>
          </div>
        </section>

        <section className="section section-gallery height-wide">
          <div className="pic-block n1 right">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/method/soil/gallery/pic-2.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                北海道本別町、福田農場の牛舎にて。一頭一頭の食べたものが、土をつくる材料になる。
              </div>
            </InViewAnimation>
          </div>
          <div className="pic-block n2 left">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/method/soil/gallery/pic-3.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                籾殻と腐植資材。畑に還り、土を豊かに整える。
              </div>
            </InViewAnimation>
          </div>
        </section>

        <section id="SECTION-3" className="section section-famar-idea">
          <div className="accordion-contianer-wrap">
            <AccordionLarge>
              <div className="idea-title bold">
                自然界の多様性を<br className="spOnly" />畑に再現する意義
              </div>
              <div className="pic-idea-profile">
                <img src="/assets/img/method/soil/pic-idea-profile.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-idea-company">
                <img src="/assets/img/method/soil/pic-idea-company.png" alt="" loading="lazy" />
              </div>
              <div className="idea-profile-position bold">
                国立研究開発法人 理化学研究所 <br />
                環境資源科学研究センター <br className="spOnly" />ホロビオント・レジリエンス研究チーム<br />
                チームディレクター
              </div>
              <div className="idea-profile-name bold">
                市橋 泰範さん
              </div>

              <div className="accordion-content">
                <div className="accordion-content-inner">
                  <div className="text">
                    <span className="bold">大きな一歩</span>
                    <br />
                    この数年間、日本の農業における各分野の第一線で活躍されている研究者や生産者の皆さまと共に、チームとして社会変革に向けた実践的な議論を重ねてきました。そして今、社会実証に向けて、取り組みがいよいよ始まったことは、日本の農業にとってこれまでにない挑戦であり、持続可能な未来への確かな第一歩であると感じています。この取組みは、現場と科学が連携する新しい農業のあり方を示すものとして、次世代に希望をつなぐ礎になると信じています。
                    <br />
                    <br />
                    <span className="bold">取り組みへの期待</span>
                    <br />
                    BGが掲げる「自然の多様性を土に取り戻す」という視点、そしてその実現に向けた多様なアプローチには大きな可能性を感じています。特に、土壌微生物の多様性は、作物の収量や品質を高めるうえで極めて重要な鍵を握っており、これは化学肥料や農薬への依存を減らし、環境と調和した農業を目指すうえでも欠かせない視点です。土に宿る見えない力を引き出すというこの試みに、大きな期待を寄せています。
                    <br />
                    <br />
                    <span className="bold">微生物と植物の関係性に関する研究の意義</span>
                    <br />
                    微生物と植物の相互作用を理解する研究は、今や日本にとどまらず世界の研究者が注目するホットな領域です。BGとの共同研究は、土壌—微生物—植物の関係性を多面的に捉え、新しい評価指標や資材開発にも挑戦する、極めて意義深い取り組みとなっています。この研究を通じて、微生物の力を引き出す知見と技術が確立されれば、それは日本の農業の未来にとどまらず、持続可能な地球規模の農業への貢献につながると信じています。
                    <br />
                    <br />
                    <span className="bold">今後に向けて</span>
                    <br />
                    今後も、BG、篤農家、そして研究チームの皆さまと共に、日本のどこでも誰でも、簡便に実践できる持続可能な農業の実現に向けて研究を続けてまいります。科学と現場が手を携え、一歩ずつ積み重ねていくこの歩みが、多くの人にとって農業の新しい希望の光となることを願っています。
                  </div>
                </div>
              </div>
            </AccordionLarge>
          </div>
        </section>

        {posts.length > 0 && (
          <>
            <div className="separator-asterisk en">*</div>

            <section className="section section-method-latestcase">
              <div className="section-title-holder">
                <h2 className="section-title bold">
                  最新事例
                </h2>
              </div>
              <div className="container-xs grayCard">
                <NewsList posts={posts} />
                <Link href="/news/implementation/soil_solution/" className="button-link button-round scaleHover size-s">
                  <span className="inner">すべての事例をみる</span>
                </Link>
              </div>
            </section>
          </>
        )}
      </div>
        
      <section className="section section-method-others">
        <div className="container-l">
          <MethodCards currentNumber={0} className="scaleHover" />
        </div>
      </section>
    </main>
  );
};

export default MethodSection;
